import { useState, type FormEvent } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { supabase } from "../lib/supabase";

export function LoginPage() {
  const { user, loading, configured, signIn } = useAuth();
  const [searchParams] = useSearchParams();
  const requestedReturnTo = searchParams.get("returnTo") ?? "/";
  const returnTo =
    requestedReturnTo.startsWith("/") && !requestedReturnTo.startsWith("//")
      ? requestedReturnTo
      : "/";

  const [mode, setMode] = useState<"login" | "recovery">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [recoverySubmitting, setRecoverySubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  if (loading) {
    return (
      <main className="center-screen">
        <div className="loading-card">Cargando…</div>
      </main>
    );
  }

  if (user) {
    return <Navigate to={returnTo} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setMessage(null);

    if (!configured) {
      setError("Falta configurar Supabase en las variables de entorno.");
      return;
    }

    setSubmitting(true);
    const signInError = await signIn(email, password);
    setSubmitting(false);

    if (signInError) {
      setError(signInError);
    }
  }

  async function handleRecovery(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setMessage(null);

    if (!configured) {
      setError("Falta configurar Supabase en las variables de entorno.");
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      setError("Ingresa tu correo.");
      return;
    }

    setRecoverySubmitting(true);

    const { error: recoveryError } =
      await supabase.auth.resetPasswordForEmail(normalizedEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

    setRecoverySubmitting(false);

    if (recoveryError) {
      setError(recoveryError.message);
      return;
    }

    setMessage(
      "Si el correo está registrado, recibirás un enlace para crear una nueva contraseña.",
    );
  }

  return (
    <main className="login-page">
      <section className="login-panel">
        <div className="login-heading">
          <span className="eyebrow">CORE SaaS · QA</span>
          <h1>
            {mode === "login"
              ? "Acceso multiempresa"
              : "Recuperar acceso"}
          </h1>
          <p>
            {mode === "login"
              ? "Ingresa para continuar al panel operativo."
              : "Te enviaremos un enlace seguro para establecer una nueva contraseña."}
          </p>
        </div>

        {mode === "login" ? (
          <form className="login-form" onSubmit={handleSubmit}>
            <label>
              Correo
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>

            <label>
              Contraseña
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>

            {error ? <div className="form-error">{error}</div> : null}
            {message ? <div className="form-success">{message}</div> : null}

            <button
              className="primary-button"
              type="submit"
              disabled={submitting}
            >
              {submitting ? "Ingresando…" : "Ingresar"}
            </button>

            <button
              className="login-secondary-action"
              type="button"
              onClick={() => {
                setMode("recovery");
                setError(null);
                setMessage(null);
                setPassword("");
              }}
            >
              Olvidé mi contraseña
            </button>
          </form>
        ) : (
          <form className="login-form" onSubmit={handleRecovery}>
            <label>
              Correo
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>

            {error ? <div className="form-error">{error}</div> : null}
            {message ? <div className="form-success">{message}</div> : null}

            <button
              className="primary-button"
              type="submit"
              disabled={recoverySubmitting}
            >
              {recoverySubmitting
                ? "Enviando…"
                : "Enviar enlace de recuperación"}
            </button>

            <button
              className="login-secondary-action"
              type="button"
              onClick={() => {
                setMode("login");
                setError(null);
                setMessage(null);
              }}
            >
              Volver a ingresar
            </button>
          </form>
        )}
      </section>
    </main>
  );
}
